import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, GraduationCap, Mail, Phone, MapPin, ArrowRight, Facebook, Sparkles, MessageCircle, Clock, Youtube } from 'lucide-react';
import { personalInfo, socialLinks } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setHoveredItem(null);
  }, [location]);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Experience', path: '/experience' },
    { label: 'Publications', path: '/publications' },
    { label: 'Projects', path: '/projects' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Top Bar - Professional Animated Design */}
      <div className={`relative overflow-hidden transition-all duration-500 ${isScrolled ? 'opacity-0 -translate-y-full h-0' : 'opacity-100 translate-y-0'}`}>
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1F3D] via-[#1F3C88] to-[#0F1F3D]" />
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '20px 20px' }} />
        
        <div className="relative z-10 py-2.5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between">
              {/* Left Side - Contact Info */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Email */}
                <a 
                  href={`mailto:${personalInfo.email}`} 
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 hover:from-blue-500 hover:to-indigo-600 border border-white/10 hover:border-transparent transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg group-hover:animate-pulse">
                    <Mail className="w-3 h-3 text-white" />
                  </div>
                  <span className="hidden lg:inline text-white text-xs font-semibold">{personalInfo.email}</span>
                </a>

                {/* Phone */}
                <a 
                  href={`tel:${personalInfo.phone}`} 
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 hover:from-emerald-500 hover:to-green-600 border border-white/10 hover:border-transparent transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/25"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 flex items-center justify-center shadow-lg group-hover:animate-pulse">
                    <Phone className="w-3 h-3 text-white" />
                  </div>
                  <span className="hidden lg:inline text-white text-xs font-semibold">{personalInfo.phone}</span>
                </a>

                {/* WhatsApp */}
                <a 
                  href="https://wa.me/252634453948"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#25D366]/20 to-[#128C7E]/20 hover:from-[#25D366] hover:to-[#128C7E] border border-white/10 hover:border-transparent transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/25"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] flex items-center justify-center shadow-lg group-hover:animate-pulse">
                    <MessageCircle className="w-3 h-3 text-white" />
                  </div>
                  <span className="hidden xl:inline text-white text-xs font-semibold">WhatsApp</span>
                </a>
              </div>

              {/* Center - Animated Badge */}
              <div className="hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30">
                <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
                <span className="text-amber-300 text-xs font-bold">PhD Sociologist & Academic Leader</span>
                <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
              </div>

              {/* Right Side - Location & Social */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Location */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-white/10">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                    <MapPin className="w-3 h-3 text-white" />
                  </div>
                  <span className="hidden sm:inline text-white text-xs font-semibold">Hargeisa</span>
                </div>

                {/* Divider */}
                <div className="hidden sm:block w-px h-6 bg-gradient-to-b from-transparent via-white/20 to-transparent" />

                {/* Facebook */}
                <a 
                  href="https://www.facebook.com/saki.bade.5" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#1877F2]/20 to-[#0D65D9]/20 hover:from-[#1877F2] hover:to-[#0D65D9] border border-white/10 hover:border-transparent transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#1877F2] to-[#0D65D9] flex items-center justify-center shadow-lg group-hover:animate-pulse">
                    <Facebook className="w-3 h-3 text-white" />
                  </div>
                  <span className="hidden sm:inline text-white text-xs font-semibold">Follow</span>
                </a>

                {/* YouTube */}
                <a 
                  href={socialLinks.youtube}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-red-500/20 to-pink-500/20 hover:from-red-500 hover:to-pink-500 border border-white/10 hover:border-transparent transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/25"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center shadow-lg group-hover:animate-pulse">
                    <Youtube className="w-3 h-3 text-white" />
                  </div>
                  <span className="hidden sm:inline text-white text-xs font-semibold">YouTube</span>
                </a>

                {/* TikTok */}
                <a 
                  href={socialLinks.tiktok}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-gray-800/20 to-gray-900/20 hover:from-gray-800 hover:to-gray-900 border border-white/10 hover:border-transparent transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gray-500/25"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center shadow-lg group-hover:animate-pulse">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <span className="hidden sm:inline text-white text-xs font-semibold">TikTok</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Gradient Line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5 py-3'
            : 'bg-white py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className={`relative transition-all duration-300 ${isScrolled ? 'w-11 h-11' : 'w-12 h-12'}`}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#1F3C88] to-[#2A4FA8] rounded-xl transform group-hover:rotate-6 group-hover:scale-105 transition-all duration-300 shadow-lg" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-[#C9A24D] to-[#D9B76A] rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-[7px] font-bold">PhD</span>
                </div>
              </div>
              <div className="hidden sm:block">
                <span className="text-[#1F3C88] font-bold text-lg block leading-tight group-hover:text-[#2A4FA8] transition-colors">
                  Dr. Zaki Bade
                </span>
                <span className="text-[#C9A24D] text-xs font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Sociologist & Researcher
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Advanced Animated Menu */}
            <nav className="hidden lg:flex items-center">
              <div className="flex items-center bg-gray-50/80 rounded-full p-1.5 shadow-inner">
                {navItems.map((item, index) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    className="relative px-4 py-2 font-medium text-sm transition-all duration-300"
                    onMouseEnter={() => setHoveredItem(index)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {/* Active/Hover Background */}
                    <span 
                      className={`absolute inset-0 rounded-full transition-all duration-300 ${
                        location.pathname === item.path 
                          ? 'bg-gradient-to-r from-[#1F3C88] to-[#2A4FA8] shadow-lg shadow-[#1F3C88]/25' 
                          : hoveredItem === index 
                            ? 'bg-[#1F3C88]/10 scale-100' 
                            : 'bg-transparent scale-95'
                      }`}
                    />
                    {/* Label */}
                    <span 
                      className={`relative z-10 transition-colors duration-300 ${
                        location.pathname === item.path 
                          ? 'text-white font-semibold' 
                          : 'text-[#4A5568] hover:text-[#1F3C88]'
                      }`}
                    >
                      {item.label}
                    </span>
                    {/* Underline Animation for non-active items */}
                    {location.pathname !== item.path && (
                      <span 
                        className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#C9A24D] rounded-full transition-all duration-300 ${
                          hoveredItem === index ? 'w-6 opacity-100' : 'w-0 opacity-0'
                        }`}
                      />
                    )}
                  </Link>
                ))}
              </div>
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="group relative overflow-hidden flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#C9A24D] to-[#D9B76A] rounded-full shadow-lg shadow-[#C9A24D]/25 hover:shadow-xl hover:shadow-[#C9A24D]/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-[#D9B76A] to-[#C9A24D] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative">Get in Touch</span>
                <ArrowRight className="relative w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden relative w-12 h-12 flex items-center justify-center rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-5">
                <span className={`absolute left-0 w-full h-0.5 bg-[#1F3C88] rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'top-2 rotate-45' : 'top-0'}`} />
                <span className={`absolute left-0 top-2 w-full h-0.5 bg-[#1F3C88] rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`} />
                <span className={`absolute left-0 w-full h-0.5 bg-[#1F3C88] rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'top-2 -rotate-45' : 'top-4'}`} />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Full Screen Overlay */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${isMobileMenuOpen ? 'visible' : 'invisible pointer-events-none'}`}>
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-[#1F3C88]/90 backdrop-blur-md transition-opacity duration-500 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div className={`absolute top-0 right-0 w-full max-w-sm h-full bg-white shadow-2xl transition-transform duration-500 ease-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1F3C88] to-[#2A4FA8] rounded-xl flex items-center justify-center shadow-lg">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-[#1F3C88] font-bold block">Dr. Zaki Bade</span>
                  <span className="text-[#C9A24D] text-sm font-medium">PhD Sociology</span>
                </div>
              </div>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="w-5 h-5 text-[#1F3C88]" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-6 py-8 overflow-y-auto">
              <div className="space-y-2">
                {navItems.map((item, index) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`group flex items-center gap-4 px-4 py-4 rounded-xl font-medium transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-[#1F3C88] to-[#2A4FA8] text-white shadow-lg'
                        : 'text-[#4A5568] hover:bg-gray-50 hover:text-[#1F3C88]'
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-white/20'
                        : 'bg-[#1F3C88]/5 group-hover:bg-[#1F3C88]/10'
                    }`}>
                      <span className="text-lg">
                        {item.label === 'Home' && '🏠'}
                        {item.label === 'About' && '👤'}
                        {item.label === 'Experience' && '💼'}
                        {item.label === 'Publications' && '📚'}
                        {item.label === 'Projects' && '🎯'}
                        {item.label === 'Gallery' && '🖼️'}
                        {item.label === 'Contact' && '✉️'}
                      </span>
                    </span>
                    <span className="flex-1">{item.label}</span>
                    <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                    }`} />
                  </Link>
                ))}
              </div>
            </nav>

            {/* Bottom Section */}
            <div className="p-6 border-t border-gray-100 bg-gray-50/50">
              <div className="space-y-4">
                <a href={`mailto:${personalInfo.email}`} className="flex items-center gap-3 text-sm text-[#4A5568] hover:text-[#1F3C88] transition-colors">
                  <span className="w-10 h-10 rounded-lg bg-[#1F3C88]/10 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#1F3C88]" />
                  </span>
                  <span className="font-medium">{personalInfo.email}</span>
                </a>
                <a href={`tel:${personalInfo.phone}`} className="flex items-center gap-3 text-sm text-[#4A5568] hover:text-[#1F3C88] transition-colors">
                  <span className="w-10 h-10 rounded-lg bg-[#1F3C88]/10 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#1F3C88]" />
                  </span>
                  <span className="font-medium">{personalInfo.phone}</span>
                </a>
                <a 
                  href="https://www.facebook.com/saki.bade.5" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-[#4A5568] hover:text-[#1877F2] transition-colors"
                >
                  <span className="w-10 h-10 rounded-lg bg-[#1877F2]/10 flex items-center justify-center">
                    <Facebook className="w-4 h-4 text-[#1877F2]" />
                  </span>
                  <span className="font-medium">Follow on Facebook</span>
                </a>
              </div>

              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-6 flex items-center justify-center gap-2 w-full px-4 py-4 text-sm font-semibold text-white bg-gradient-to-r from-[#1F3C88] to-[#2A4FA8] rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
